import { useCallback, useMemo } from 'react';
import type { RangeConnectorParams, RangeWidgetDescription } from 'instantsearch.js/es/connectors/range/connectRange';
import connectRange from 'instantsearch.js/es/connectors/range/connectRange';
import type { RefinementListItem } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList';
import type { UseRefinementListProps } from 'react-instantsearch';
import { useConnector, useRefinementList } from 'react-instantsearch';
import { ALGOLIA_ATTRIBUTES } from '@shared/utils/src/algolia/algoliaAttributes';
import { getLocalizedAttribute } from '../utils/algoliaFilterUtils/getLocalizedAttribute';

// When using the useRefinementList in the component.
// If the component get destroyed or unmounted the refinement list will reset
// The solution is just create custom hook that will be Filter container

interface RatingRefinement extends RefinementListItem {
  label: string;
  value: string;
  count: number;
}

function useRangeSlider(props?: RangeConnectorParams) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useConnector<RangeConnectorParams, RangeWidgetDescription>(connectRange as any, props);
}

// Defining an item to hide one size button which was originally "0" size. No need for it to be displayed in PLP filters - CICD-9146
const ONE_SIZE_LIST_ITEM = 'one size_';

export const useAlgoliaFilter = (_locale: string) => {
  const refinementConfig: Partial<UseRefinementListProps> = {
    operator: 'or',
    limit: 6,
    showMore: true,
    showMoreLimit: 100,
  };

  const sizeRefinements = useRefinementList({
    attribute: ALGOLIA_ATTRIBUTES.SIZE,
    operator: 'or',
    limit: 100,
    transformItems: useCallback(
      (items: RefinementListItem[]) => items.filter((item) => item.value.toLowerCase() !== ONE_SIZE_LIST_ITEM),
      [],
    ),
  });

  const priceRange = useRangeSlider({
    attribute: ALGOLIA_ATTRIBUTES.PRICE_CENT_AMOUNT,
  });

  const fasteningRefinements = useRefinementList({
    attribute: getLocalizedAttribute(ALGOLIA_ATTRIBUTES.FASTENING, _locale),
    ...refinementConfig,
  });

  const genderRefinements = useRefinementList({
    attribute: getLocalizedAttribute(ALGOLIA_ATTRIBUTES.GENDER, _locale),
    ...refinementConfig,
  });

  const productTypeRefinements = useRefinementList({
    attribute: getLocalizedAttribute(ALGOLIA_ATTRIBUTES.PRODUCT_TYPE, _locale),
    ...refinementConfig,
  });

  const brandRefinements = useRefinementList({
    attribute: getLocalizedAttribute(ALGOLIA_ATTRIBUTES.BRAND, _locale),
    ...refinementConfig,
  });

  const technologiesRefinements = useRefinementList({
    attribute: getLocalizedAttribute(ALGOLIA_ATTRIBUTES.TECHNOLOGIES, _locale),
    ...refinementConfig,
  });

  const materialsRefinements = useRefinementList({
    attribute: getLocalizedAttribute(ALGOLIA_ATTRIBUTES.UPPER_MATERIAL, _locale),
    ...refinementConfig,
  });

  const ratingRefinements = useRefinementList({
    attribute: getLocalizedAttribute(ALGOLIA_ATTRIBUTES.REVIEW_RATING, _locale),
    transformItems: useCallback(
      (items: RatingRefinement[]) =>
        items.sort((a, b) => Number(b.value) - Number(a.value)).map((item) => ({ ...item })),
      [],
    ),
    ...refinementConfig,
  });

  const widthRefinements = useRefinementList({
    attribute: getLocalizedAttribute(ALGOLIA_ATTRIBUTES.FIT, _locale),
    ...refinementConfig,
  });

  const styleRefinements = useRefinementList({
    attribute: getLocalizedAttribute(ALGOLIA_ATTRIBUTES.PRODUCT_SUB_TYPES, _locale),
    ...refinementConfig,
  });

  const colorRefinements = useRefinementList({
    attribute: getLocalizedAttribute(ALGOLIA_ATTRIBUTES.COLOR, _locale),
    ...refinementConfig,
  });

  const heelHeightRefinements = useRefinementList({
    attribute: getLocalizedAttribute(ALGOLIA_ATTRIBUTES.HEEL_HEIGHT_GROUPS, _locale),
    operator: 'and',
    ...refinementConfig,
  });

  const isFiltersEmpty = useMemo(() => {
    const itemLengths =
      sizeRefinements.items.length +
      colorRefinements.items.length +
      styleRefinements.items.length +
      fasteningRefinements.items.length +
      materialsRefinements.items.length +
      ratingRefinements.items.length +
      widthRefinements.items.length +
      technologiesRefinements.items.length +
      productTypeRefinements.items.length +
      heelHeightRefinements.items.length +
      genderRefinements.items.length +
      brandRefinements.items.length;

    return itemLengths === 0;
  }, [
    sizeRefinements.items.length,
    colorRefinements.items.length,
    styleRefinements.items.length,
    fasteningRefinements.items.length,
    materialsRefinements.items.length,
    ratingRefinements.items.length,
    widthRefinements.items.length,
    technologiesRefinements.items.length,
    productTypeRefinements.items.length,
    heelHeightRefinements.items.length,
    genderRefinements.items.length,
    brandRefinements.items.length,
  ]);
  return {
    sizeRefinements,
    priceRange,
    colorRefinements,
    styleRefinements,
    fasteningRefinements,
    materialsRefinements,
    ratingRefinements,
    widthRefinements,
    technologiesRefinements,
    productTypeRefinements,
    heelHeightRefinements,
    genderRefinements,
    brandRefinements,
    isFiltersEmpty,
  };
};

export default useAlgoliaFilter;

export type UseAlgoliaFilterReturn = ReturnType<typeof useAlgoliaFilter>;
